import { useMemo } from 'react';

import { formatUnits, type Address } from 'viem';
import { useAccount, useReadContract } from 'wagmi';

import { poolContractAbi, erc20Abi } from '@/abi';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { C_USDC_CONTRACT } from '@/ts/constants/C_USDC_CONTRACT';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';

interface I_UsePoolsTokenBalancesProps {
  poolContractAddress: Address | undefined;
  tokenAddress: Address | undefined;
  tokenDecimals: number;
  usdcAddress?: Address;
}

export function usePoolsTokenBalances({
  poolContractAddress,
  tokenAddress,
  tokenDecimals,
  usdcAddress
}: I_UsePoolsTokenBalancesProps) {
  const { address } = useAccount();

  // Get USDC address from pool contract
  const { data: contractUsdcAddress } = useReadContract({
    address: poolContractAddress,
    abi: poolContractAbi,
    functionName: 'usdc',
    query: { enabled: !!poolContractAddress }
  }) as { data: Address | undefined };

  const effectiveUsdcAddress = usdcAddress || contractUsdcAddress;

  // USDC balance
  const { data: usdcBalance, refetch: refetchUsdcBalance } = useReadContract({
    address: effectiveUsdcAddress || C_USDC_CONTRACT,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && (!!effectiveUsdcAddress || !!C_USDC_CONTRACT)
    }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  // Token balance
  const { data: tokenBalance, refetch: refetchTokenBalance } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address && !!tokenAddress }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  // Read actual token decimals from contract
  const { data: contractTokenDecimals } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'decimals',
    query: { enabled: !!tokenAddress }
  }) as { data: number | undefined };

  // Use contract decimals if available, fallback to pool decimals
  const actualTokenDecimals = contractTokenDecimals ?? tokenDecimals;

  // Format balances
  const formattedUsdcBalance = useMemo(() => {
    if (!usdcBalance) return '0';
    return formatNumberWithCommasAndDecimals(
      formatUnits(usdcBalance, C_USDC_DECIMALS),
      C_USDC_DECIMALS
    );
  }, [usdcBalance]);

  const formattedTokenBalance = useMemo(() => {
    if (!tokenBalance) return '0';
    const balanceFromTokenDecimals = formatUnits(
      tokenBalance,
      actualTokenDecimals
    );
    return formatNumberWithCommasAndDecimals(
      balanceFromTokenDecimals,
      tokenDecimals
    );
  }, [tokenBalance, actualTokenDecimals, tokenDecimals]);

  return {
    usdcBalance,
    tokenBalance,
    actualTokenDecimals,
    formattedUsdcBalance,
    formattedTokenBalance,
    refetchUsdcBalance,
    refetchTokenBalance
  };
}
